import { useEffect, useMemo, useState } from "react";
import {
  BookUser,
  Loader2,
  Plus,
  Search,
  Trash2,
  Upload,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { ContactAvatar } from "@/features/contacts/ContactAvatar";
import { MailModalOverlay } from "@/features/shared/MailModalOverlay";
import { MailLoadingState } from "@/features/shared/MailLoadingState";
import { MailCircularProgress } from "@/features/shared/MailCircularProgress";
import { useMailContacts } from "@/hooks/useMailContacts";
import { mailContactDisplayLabel } from "@/lib/mail/contactLabels";
import type { MailContact } from "@/lib/types/contact";
import type { MailContactGroup, MailContactGroupMember } from "@/lib/types/contactGroup";
import { cn } from "@/lib/cn";

type Props = {
  open: boolean;
  onClose: () => void;
};

type Tab = "collected" | "book" | "groups";

function memberFromContact(contact: MailContact): MailContactGroupMember {
  return {
    email: contact.email,
    displayName: mailContactDisplayLabel(contact),
    contactId: contact.id,
  };
}

export function AddressBookModal({ open, onClose }: Props) {
  const {
    contacts,
    groups,
    loading,
    refresh,
    promoteToBook,
    saveContact,
    removeContact,
    uploadPhoto,
    saveGroup,
    removeGroup,
  } = useMailContacts();
  const [tab, setTab] = useState<Tab>("collected");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<MailContact | null>(null);
  const [creatingContact, setCreatingContact] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [editingGroup, setEditingGroup] = useState<MailContactGroup | null>(null);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupMembers, setGroupMembers] = useState<MailContactGroupMember[]>([]);
  const [memberQuery, setMemberQuery] = useState("");
  const [manualMemberEmail, setManualMemberEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const busy = saving || photoUploading;
  const isGroupsTab = tab === "groups";
  const isBookTab = tab === "book";

  function isValidEmail(raw: string): boolean {
    const email = raw.trim().toLowerCase();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  useEffect(() => {
    if (!open) {
      setQuery("");
      setEditing(null);
      setCreatingContact(false);
      setNewEmail("");
      setEditingGroup(null);
      setCreatingGroup(false);
      setGroupName("");
      setGroupMembers([]);
      setMemberQuery("");
      setManualMemberEmail("");
      setError(null);
      setStatusMessage(null);
      setPhotoUploading(false);
      setSaving(false);
      return;
    }
    void refresh();
  }, [open, refresh]);

  useEffect(() => {
    if (!editing || creatingContact) {
      if (!creatingContact) {
        setFirstName("");
        setLastName("");
        setCompany("");
      }
      return;
    }
    setFirstName(editing.firstName ?? "");
    setLastName(editing.lastName ?? "");
    setCompany(editing.company ?? "");
    setError(null);
  }, [creatingContact, editing]);

  useEffect(() => {
    if (!statusMessage) return;
    const timer = window.setTimeout(() => setStatusMessage(null), 3200);
    return () => window.clearTimeout(timer);
  }, [statusMessage]);

  const filteredContacts = useMemo(() => {
    if (isGroupsTab) return [];
    const q = query.trim().toLowerCase();
    return contacts
      .filter((c) => c.status === tab)
      .filter((c) => {
        if (!q) return true;
        const label = mailContactDisplayLabel(c).toLowerCase();
        return label.includes(q) || c.email.toLowerCase().includes(q);
      })
      .sort((a, b) => (b.lastSeenAt ?? "").localeCompare(a.lastSeenAt ?? ""));
  }, [contacts, isGroupsTab, query, tab]);

  const filteredGroups = useMemo(() => {
    if (!isGroupsTab) return [];
    return [...groups].sort((a, b) => a.name.localeCompare(b.name, "fr"));
  }, [groups, isGroupsTab]);

  const memberCandidates = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    const selected = new Set(groupMembers.map((m) => m.email.toLowerCase()));
    return contacts
      .filter((c) => !selected.has(c.email.toLowerCase()))
      .filter((c) => {
        if (!q) return true;
        const label = mailContactDisplayLabel(c).toLowerCase();
        return label.includes(q) || c.email.toLowerCase().includes(q);
      })
      .sort((a, b) => {
        if (a.status !== b.status) return a.status === "book" ? -1 : 1;
        return mailContactDisplayLabel(a).localeCompare(mailContactDisplayLabel(b), "fr");
      })
      .slice(0, 40);
  }, [contacts, groupMembers, memberQuery]);

  function openCreateGroup() {
    setCreatingGroup(true);
    setEditingGroup(null);
    setEditing(null);
    setCreatingContact(false);
    setNewEmail("");
    setGroupName("");
    setGroupMembers([]);
    setMemberQuery("");
    setManualMemberEmail("");
    setError(null);
  }

  function openCreateContact() {
    setCreatingContact(true);
    setEditing(null);
    setEditingGroup(null);
    setCreatingGroup(false);
    setNewEmail("");
    setFirstName("");
    setLastName("");
    setCompany("");
    setError(null);
    setTab("book");
  }

  function openEditGroup(group: MailContactGroup) {
    setCreatingGroup(false);
    setEditingGroup(group);
    setEditing(null);
    setCreatingContact(false);
    setGroupName(group.name);
    setGroupMembers(group.members.map((m) => ({ ...m })));
    setMemberQuery("");
    setManualMemberEmail("");
    setError(null);
  }

  function addMember(contact: MailContact) {
    const next = memberFromContact(contact);
    setGroupMembers((prev) => {
      if (prev.some((m) => m.email.toLowerCase() === next.email.toLowerCase())) return prev;
      return [...prev, next].sort((a, b) => a.email.localeCompare(b.email));
    });
  }

  function addManualMember() {
    const email = manualMemberEmail.trim().toLowerCase();
    if (!isValidEmail(email)) {
      setError("Adresse e-mail invalide.");
      return;
    }
    setError(null);
    setGroupMembers((prev) => {
      if (prev.some((m) => m.email.toLowerCase() === email)) return prev;
      const known = contacts.find((c) => c.email.toLowerCase() === email);
      const next: MailContactGroupMember = known
        ? memberFromContact(known)
        : { email, displayName: null, contactId: null };
      return [...prev, next].sort((a, b) => a.email.localeCompare(b.email));
    });
    setManualMemberEmail("");
  }

  function removeMember(email: string) {
    const key = email.toLowerCase();
    setGroupMembers((prev) => prev.filter((m) => m.email.toLowerCase() !== key));
  }

  async function handleSaveGroup() {
    const name = groupName.trim();
    if (!name) {
      setError("Indiquez un nom de groupe.");
      return;
    }
    setSaving(true);
    setError(null);
    setStatusMessage(null);
    const saved = await saveGroup({
      id: editingGroup?.id,
      name,
      members: groupMembers,
    });
    setSaving(false);
    if (!saved) {
      setError("Impossible d'enregistrer le groupe.");
      return;
    }
    setEditingGroup(saved);
    setCreatingGroup(false);
    setGroupName(saved.name);
    setGroupMembers(saved.members.map((m) => ({ ...m })));
    setStatusMessage(editingGroup ? "Groupe enregistré" : "Groupe créé");
  }

  async function handleDeleteGroup(group: MailContactGroup) {
    if (!window.confirm(`Supprimer le groupe « ${group.name} » ?`)) return;
    const ok = await removeGroup(group.id);
    if (!ok) {
      setError("Impossible de supprimer le groupe.");
      return;
    }
    if (editingGroup?.id === group.id) {
      setEditingGroup(null);
      setCreatingGroup(false);
      setGroupName("");
      setGroupMembers([]);
    }
  }

  async function handleSaveContact() {
    setSaving(true);
    setError(null);
    setStatusMessage(null);

    if (creatingContact) {
      const email = newEmail.trim().toLowerCase();
      if (!isValidEmail(email)) {
        setSaving(false);
        setError("Adresse e-mail invalide.");
        return;
      }
      const saved = await saveContact({
        email,
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        company: company.trim() || null,
        status: "book",
      });
      setSaving(false);
      if (!saved) {
        setError("Impossible de créer le contact.");
        return;
      }
      setCreatingContact(false);
      setNewEmail("");
      setEditing(saved);
      setTab("book");
      setStatusMessage("Contact ajouté au carnet");
      return;
    }

    if (!editing) {
      setSaving(false);
      return;
    }

    const payload = {
      email: editing.email,
      firstName: firstName.trim() || null,
      lastName: lastName.trim() || null,
      company: company.trim() || null,
      displayName: editing.displayName,
    };

    const wasCollected = editing.status === "collected";
    const saved = wasCollected
      ? await promoteToBook(editing, payload)
      : await saveContact({ ...payload, id: editing.id, status: "book" });

    setSaving(false);
    if (!saved) {
      setError("Impossible d'enregistrer le contact.");
      return;
    }
    setEditing(saved);
    if (wasCollected) setTab("book");
    setStatusMessage(wasCollected ? "Contact ajouté au carnet" : "Contact enregistré");
  }

  async function handlePhotoChange(file: File | null) {
    if (!editing || !file) return;
    setPhotoUploading(true);
    setError(null);
    setStatusMessage(null);

    let target = editing;
    if (editing.status === "collected") {
      const promoted = await promoteToBook(editing, {
        email: editing.email,
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        company: company.trim() || null,
        displayName: editing.displayName,
      });
      if (!promoted) {
        setPhotoUploading(false);
        setError("Ajoutez d'abord le contact au carnet.");
        return;
      }
      target = promoted;
      setEditing(promoted);
      setTab("book");
    }

    const updated = await uploadPhoto(target.id, file);
    setPhotoUploading(false);
    if (!updated) {
      setError("Photo non enregistrée.");
      return;
    }
    setEditing(updated);
    setStatusMessage("Photo enregistrée");
  }

  async function handleDelete(contact: MailContact) {
    if (!window.confirm(`Supprimer ${contact.email} ?`)) return;
    await removeContact(contact.id);
    if (editing?.id === contact.id) setEditing(null);
  }

  function switchTab(next: Tab) {
    setTab(next);
    setQuery("");
    setEditing(null);
    setCreatingContact(false);
    setNewEmail("");
    setEditingGroup(null);
    setCreatingGroup(false);
    setGroupName("");
    setGroupMembers([]);
    setMemberQuery("");
    setManualMemberEmail("");
    setError(null);
  }

  const groupEditorOpen = isGroupsTab && (creatingGroup || editingGroup != null);
  const contactEditorOpen = creatingContact || editing != null;

  return (
    <MailModalOverlay open={open} onClose={onClose} labelledBy="address-book-title" panelClassName="max-w-2xl">
      <div className="relative flex max-h-[min(85vh,720px)] min-h-0 flex-col">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)]"
          aria-label="Fermer le carnet d'adresses"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-3 border-b border-[var(--mail-border-soft)] px-5 py-4 pr-12">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--mail-accent-soft)] text-[var(--mail-accent)]">
            <BookUser className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="address-book-title" className="text-base font-semibold text-[var(--mail-text)]">
              Carnet d&apos;adresses
            </h2>
            <p className="text-xs text-[var(--mail-muted)]">
              Collectés · carnet · groupes pour rédiger à plusieurs destinataires
            </p>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap gap-2 border-b border-[var(--mail-border-soft)] px-5 py-3">
          <button
            type="button"
            onClick={() => switchTab("collected")}
            className={cn(
              "rounded-full px-3 py-1.5 text-xs font-medium transition",
              tab === "collected"
                ? "bg-[var(--mail-accent-soft)] text-[var(--mail-accent)]"
                : "text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)]"
            )}
          >
            Collectés ({contacts.filter((c) => c.status === "collected").length})
          </button>
          <button
            type="button"
            onClick={() => switchTab("book")}
            className={cn(
              "rounded-full px-3 py-1.5 text-xs font-medium transition",
              tab === "book"
                ? "bg-[var(--mail-accent-soft)] text-[var(--mail-accent)]"
                : "text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)]"
            )}
          >
            Carnet ({contacts.filter((c) => c.status === "book").length})
          </button>
          <button
            type="button"
            onClick={() => switchTab("groups")}
            className={cn(
              "rounded-full px-3 py-1.5 text-xs font-medium transition",
              tab === "groups"
                ? "bg-[var(--mail-accent-soft)] text-[var(--mail-accent)]"
                : "text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)]"
            )}
          >
            Groupes ({groups.length})
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[1fr,minmax(240px,280px)]">
          <div className="flex min-h-0 flex-col border-b border-[var(--mail-border-soft)] md:border-b-0 md:border-r">
            {!isGroupsTab ? (
              <div className="shrink-0 space-y-2 px-4 py-3">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--mail-muted)]" />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Rechercher une adresse…"
                    className="w-full rounded-full border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] py-2 pl-9 pr-3 text-sm text-[var(--mail-text)] placeholder:text-[var(--mail-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--mail-accent)]/30"
                  />
                </div>
                {isBookTab ? (
                  <button
                    type="button"
                    onClick={openCreateContact}
                    className="mail-btn-secondary-gradient inline-flex w-full items-center justify-center gap-2 rounded-full px-3 py-2 text-xs font-medium"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    Nouveau contact
                  </button>
                ) : null}
              </div>
            ) : null}

            {isGroupsTab ? (
              <div className="shrink-0 px-4 py-3">
                <button
                  type="button"
                  onClick={openCreateGroup}
                  className="mail-btn-secondary-gradient inline-flex w-full items-center justify-center gap-2 rounded-full px-3 py-2 text-xs font-medium"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Nouveau groupe
                </button>
              </div>
            ) : null}

            <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
              {loading ? (
                <MailLoadingState variant="messages" layout="compact" />
              ) : isGroupsTab ? (
                filteredGroups.length === 0 ? (
                  <p className="px-3 py-8 text-center text-sm text-[var(--mail-muted)]">
                    Aucun groupe — créez-en un pour écrire à plusieurs contacts d&apos;un coup.
                  </p>
                ) : (
                  filteredGroups.map((group) => {
                    const selected = editingGroup?.id === group.id && !creatingGroup;
                    return (
                      <div
                        key={group.id}
                        className={cn(
                          "flex items-center gap-2 rounded-[var(--mail-radius-card)] px-2 py-1.5",
                          selected && "bg-[var(--mail-accent-soft)]"
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => openEditGroup(group)}
                          className="flex min-w-0 flex-1 items-center gap-3 rounded-[var(--mail-radius-card)] px-1 py-1 text-left transition hover:bg-[var(--mail-surface-hover)]"
                        >
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--mail-accent-soft)] text-[var(--mail-accent)]">
                            <Users className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-[var(--mail-text)]">
                              {group.name}
                            </span>
                            <span className="block truncate text-xs text-[var(--mail-muted)]">
                              {group.memberCount} membre{group.memberCount > 1 ? "s" : ""}
                            </span>
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDeleteGroup(group)}
                          disabled={busy}
                          className="rounded-full p-2 text-[var(--mail-muted)] transition hover:bg-[var(--mail-danger-soft)] hover:text-[var(--mail-danger-text)] disabled:opacity-40"
                          aria-label="Supprimer le groupe"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })
                )
              ) : filteredContacts.length === 0 ? (
                <p className="px-3 py-8 text-center text-sm text-[var(--mail-muted)]">
                  {tab === "collected"
                    ? "Aucune adresse collectée pour le moment."
                    : "Votre carnet est vide — déplacez des adresses collectées."}
                </p>
              ) : (
                filteredContacts.map((contact) => {
                  const title = mailContactDisplayLabel(contact);
                  const selected = editing?.id === contact.id;
                  return (
                    <div
                      key={contact.id}
                      className={cn(
                        "flex items-center gap-2 rounded-[var(--mail-radius-card)] px-2 py-1.5",
                        selected && "bg-[var(--mail-accent-soft)]"
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setCreatingContact(false);
                          setNewEmail("");
                          setEditing(contact);
                        }}
                        className="flex min-w-0 flex-1 items-center gap-3 rounded-[var(--mail-radius-card)] px-1 py-1 text-left transition hover:bg-[var(--mail-surface-hover)]"
                      >
                        <ContactAvatar label={title} email={contact.email} size="sm" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-[var(--mail-text)]">
                            {title}
                          </span>
                          <span className="block truncate text-xs text-[var(--mail-muted)]">
                            {contact.email}
                          </span>
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(contact)}
                        disabled={busy}
                        className="rounded-full p-2 text-[var(--mail-muted)] transition hover:bg-[var(--mail-danger-soft)] hover:text-[var(--mail-danger-text)] disabled:opacity-40"
                        aria-label="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="min-h-0 overflow-y-auto p-4">
            {groupEditorOpen ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--mail-accent-soft)] text-[var(--mail-accent)]">
                    <Users className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[var(--mail-text)]">
                      {creatingGroup ? "Nouveau groupe" : "Modifier le groupe"}
                    </p>
                    <p className="text-xs text-[var(--mail-muted)]">
                      Dans Rédiger, tapez le nom — les membres sont ajoutés dans À.
                    </p>
                  </div>
                </div>

                <label className="block text-xs font-medium text-[var(--mail-muted)]">
                  Nom du groupe
                  <input
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    disabled={busy}
                    placeholder="Ex. Équipe projet"
                    className="mt-1 w-full rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2 text-sm text-[var(--mail-text)] disabled:opacity-60"
                  />
                </label>

                <div>
                  <p className="text-xs font-medium text-[var(--mail-muted)]">
                    Membres ({groupMembers.length})
                  </p>
                  {groupMembers.length === 0 ? (
                    <p className="mt-2 text-xs text-[var(--mail-muted)]">Aucun membre pour l’instant.</p>
                  ) : (
                    <ul className="mt-2 max-h-28 space-y-1 overflow-y-auto">
                      {groupMembers.map((member) => (
                        <li
                          key={member.email}
                          className="flex items-center gap-2 rounded-lg bg-[var(--mail-surface)] px-2 py-1.5"
                        >
                          <span className="min-w-0 flex-1 truncate text-xs text-[var(--mail-text)]">
                            {member.displayName && member.displayName !== member.email
                              ? `${member.displayName} · ${member.email}`
                              : member.email}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeMember(member.email)}
                            className="rounded-full p-1 text-[var(--mail-muted)] hover:bg-[var(--mail-danger-soft)] hover:text-[var(--mail-danger-text)]"
                            aria-label={`Retirer ${member.email}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-[var(--mail-muted)]">
                    Ajouter une adresse
                    <div className="mt-1 flex gap-2">
                      <input
                        type="email"
                        value={manualMemberEmail}
                        onChange={(e) => setManualMemberEmail(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addManualMember();
                          }
                        }}
                        placeholder="prenom@exemple.fr"
                        className="min-w-0 flex-1 rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2 text-sm text-[var(--mail-text)]"
                      />
                      <button
                        type="button"
                        onClick={addManualMember}
                        className="shrink-0 rounded-lg bg-[var(--mail-accent-soft)] px-3 py-2 text-xs font-medium text-[var(--mail-accent)] transition hover:opacity-90"
                      >
                        Ajouter
                      </button>
                    </div>
                  </label>
                </div>

                <div>
                  <label className="block text-xs font-medium text-[var(--mail-muted)]">
                    Ou depuis Collectés / Carnet
                    <input
                      type="search"
                      value={memberQuery}
                      onChange={(e) => setMemberQuery(e.target.value)}
                      placeholder="Rechercher…"
                      className="mt-1 w-full rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2 text-sm text-[var(--mail-text)]"
                    />
                  </label>
                  <ul className="mt-2 max-h-36 space-y-0.5 overflow-y-auto">
                    {memberCandidates.map((contact) => (
                      <li key={contact.id}>
                        <button
                          type="button"
                          onClick={() => addMember(contact)}
                          className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition hover:bg-[var(--mail-surface-hover)]"
                        >
                          <Plus className="h-3.5 w-3.5 shrink-0 text-[var(--mail-accent)]" />
                          <span className="min-w-0 flex-1 truncate text-[var(--mail-text)]">
                            {mailContactDisplayLabel(contact)}
                          </span>
                          <span className="shrink-0 text-[var(--mail-muted)]">
                            {contact.status === "book" ? "Carnet" : "Collecté"}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                {error ? <p className="text-xs text-[var(--mail-danger-text)]">{error}</p> : null}
                {statusMessage ? (
                  <p className="text-xs font-medium text-[var(--mail-accent)]">{statusMessage}</p>
                ) : null}

                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void handleSaveGroup()}
                  className="mail-btn-primary inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium disabled:opacity-60"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
                  {saving ? "Enregistrement…" : creatingGroup ? "Créer le groupe" : "Enregistrer"}
                </button>
              </div>
            ) : contactEditorOpen ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="relative shrink-0">
                    <ContactAvatar
                      label={
                        creatingContact
                          ? newEmail.trim() || "Nouveau"
                          : mailContactDisplayLabel(editing!)
                      }
                      email={creatingContact ? newEmail.trim() || undefined : editing!.email}
                      size="lg"
                    />
                    {!creatingContact && photoUploading ? (
                      <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/45">
                        <MailCircularProgress size={28} className="[&_p]:hidden" />
                      </span>
                    ) : null}
                  </div>
                  <div className="min-w-0">
                    {creatingContact ? (
                      <>
                        <p className="text-sm font-semibold text-[var(--mail-text)]">Nouveau contact</p>
                        <p className="text-xs text-[var(--mail-muted)]">Ajout manuel au carnet</p>
                      </>
                    ) : (
                      <>
                        <p className="truncate text-sm font-semibold text-[var(--mail-text)]">
                          {editing!.email}
                        </p>
                        <p className="text-xs text-[var(--mail-muted)]">
                          {editing!.status === "collected" ? "Adresse collectée" : "Contact carnet"}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {creatingContact ? (
                  <label className="block text-xs font-medium text-[var(--mail-muted)]">
                    E-mail
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      disabled={busy}
                      placeholder="prenom@exemple.fr"
                      autoFocus
                      className="mt-1 w-full rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2 text-sm text-[var(--mail-text)] disabled:opacity-60"
                    />
                  </label>
                ) : null}

                <label className="block text-xs font-medium text-[var(--mail-muted)]">
                  Prénom
                  <input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={busy}
                    className="mt-1 w-full rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2 text-sm text-[var(--mail-text)] disabled:opacity-60"
                  />
                </label>
                <label className="block text-xs font-medium text-[var(--mail-muted)]">
                  Nom
                  <input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={busy}
                    className="mt-1 w-full rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2 text-sm text-[var(--mail-text)] disabled:opacity-60"
                  />
                </label>
                <label className="block text-xs font-medium text-[var(--mail-muted)]">
                  Société
                  <input
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    disabled={busy}
                    className="mt-1 w-full rounded-lg border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2 text-sm text-[var(--mail-text)] disabled:opacity-60"
                  />
                </label>

                {!creatingContact ? (
                  <label
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-[var(--mail-border-soft)] px-3 py-3 text-sm transition",
                      photoUploading
                        ? "cursor-wait bg-[var(--mail-surface-hover)] text-[var(--mail-muted)]"
                        : "text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)]",
                      busy && !photoUploading && "pointer-events-none opacity-50"
                    )}
                  >
                    {photoUploading ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[var(--mail-accent)]" />
                    ) : (
                      <Upload className="h-4 w-4 shrink-0" />
                    )}
                    <span>{photoUploading ? "Enregistrement de la photo…" : "Logo / photo (WebP)"}</span>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="sr-only"
                      disabled={busy}
                      onChange={(e) => void handlePhotoChange(e.target.files?.[0] ?? null)}
                    />
                  </label>
                ) : null}

                {error ? <p className="text-xs text-[var(--mail-danger-text)]">{error}</p> : null}
                {statusMessage ? (
                  <p className="text-xs font-medium text-[var(--mail-accent)]">{statusMessage}</p>
                ) : null}

                {creatingContact ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void handleSaveContact()}
                    className="mail-btn-primary inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium disabled:opacity-60"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                    {saving ? "Enregistrement…" : "Créer le contact"}
                  </button>
                ) : editing!.status === "collected" ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void handleSaveContact()}
                    className="mail-btn-primary inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium disabled:opacity-60"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                    {saving ? "Enregistrement…" : "Ajouter au carnet"}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void handleSaveContact()}
                    className="mail-btn-secondary-gradient inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium disabled:opacity-60"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {saving ? "Enregistrement…" : "Enregistrer"}
                  </button>
                )}
              </div>
            ) : (
              <p className="py-8 text-center text-sm text-[var(--mail-muted)]">
                {isGroupsTab
                  ? "Créez un groupe ou sélectionnez-en un pour gérer les membres."
                  : isBookTab
                    ? "Sélectionnez un contact ou créez-en un nouveau."
                    : "Sélectionnez une adresse pour l'enrichir ou l'ajouter au carnet."}
              </p>
            )}
          </div>
        </div>
      </div>
    </MailModalOverlay>
  );
}
